// Utility functions for R-multiple calculations

export interface Trade {
  profit?: number
  entryPrice?: number
  exitPrice?: number
  sl?: number // stop loss distance (price difference)
  type?: 'buy' | 'sell'
}

// Calculate R-multiple for a single trade
export const calculateRMultiple = (trade: Trade): number => {
  const profit = trade.profit ?? 0
  const entryPrice = trade.entryPrice ?? 0
  const exitPrice = trade.exitPrice ?? 0
  const stopLossDistance = trade.sl ?? 0 // stop loss distance (price difference)
  const tradeType = trade.type

  let rMultiple: number

  if (entryPrice > 0 && exitPrice > 0 && stopLossDistance > 0) {
    // Calculate direction-aware price movement
    let priceMovement: number
    if (tradeType === 'buy') {
      // Long trade: priceMove = exit - entry
      priceMovement = exitPrice - entryPrice
    } else if (tradeType === 'sell') {
      // Short trade: priceMove = entry - exit
      priceMovement = entryPrice - exitPrice
    } else {
      // Fallback: assume long trade if type unknown
      priceMovement = exitPrice - entryPrice
    }

    // Calculate R-multiple: priceMovement / stopLossDistance
    rMultiple = priceMovement / stopLossDistance
  } else {
    // Fallback: assume 1R for winners, -1R for losers based on profit
    if (profit > 0) {
      rMultiple = 1
    } else if (profit < 0) {
      rMultiple = -1
    } else {
      rMultiple = 0
    }
  }

  return rMultiple
}

// Calculate expectancy from individual trades using proper R-multiple formula
export const calculateExpectancyFromTrades = (trades: Trade[]): number => {
  if (trades.length === 0) return 0

  const rMultiples = trades.map(calculateRMultiple)
  const winningTrades = rMultiples.filter((r) => r > 0)
  const losingTrades = rMultiples.filter((r) => r < 0)

  const winRate = winningTrades.length / trades.length
  const lossRate = 1 - winRate

  const avgWinR =
    winningTrades.length > 0
      ? winningTrades.reduce((sum, r) => sum + r, 0) / winningTrades.length
      : 0
  const avgLossR =
    losingTrades.length > 0 ? losingTrades.reduce((sum, r) => sum + r, 0) / losingTrades.length : 0

  // Proper expectancy formula: avgWinR * winRate + avgLossR * (1 - winRate)
  return avgWinR * winRate + avgLossR * lossRate
}

// Calculate win rate from individual trades using R-multiple > 0
export const calculateWinRateFromTrades = (trades: Trade[]): number => {
  if (trades.length === 0) return 0

  const rMultiples = trades.map(calculateRMultiple)
  const winningTrades = rMultiples.filter((r) => r > 0)

  return winningTrades.length / trades.length
}

// Calculate average win R from individual trades
export const calculateAvgWinR = (trades: Trade[]): number => {
  const rMultiples = trades.map(calculateRMultiple)
  const winningTrades = rMultiples.filter((r) => r > 0)

  return winningTrades.length > 0
    ? winningTrades.reduce((sum, r) => sum + r, 0) / winningTrades.length
    : 0
}

// Calculate average loss R from individual trades
export const calculateAvgLossR = (trades: Trade[]): number => {
  const rMultiples = trades.map(calculateRMultiple)
  const losingTrades = rMultiples.filter((r) => r < 0)

  return losingTrades.length > 0
    ? losingTrades.reduce((sum, r) => sum + r, 0) / losingTrades.length
    : 0
}

// Estimate expectancy from a group ratio string (e.g., "2/6" = risk 2, reward 6)
// and win/loss trade counts. Used as fallback when individual trade data is unavailable.
// Winners are assumed to have hit TP (reward/risk R), losers to have hit SL (-1R by definition).
export const estimateExpectancyFromRatio = (
  group: string,
  profitTradeCount: number,
  closedTradeCount: number
): number => {
  if (closedTradeCount <= 0) return 0

  const ratioParts = group?.split('/').map(Number)
  if (ratioParts?.length !== 2 || ratioParts[0] <= 0) return 0

  const [risk, reward] = ratioParts
  const winR = reward / risk // R gained when hitting TP (e.g., "2/6" → 3R)
  const lossR = -1 // R lost when hitting SL (always -1R by definition)

  const winRate = profitTradeCount / closedTradeCount
  const lossRate = 1 - winRate

  return winR * winRate + lossR * lossRate
}
